/**
 * Adopt an ordinary GoTrue signup confirmation without inventing a second
 * authentication ceremony.
 *
 * GoTrue returns the confirmed session in the URL fragment. A fragment stays
 * out of server logs and Referer headers, but it is still a bearer credential
 * in browser history until it is removed. Reading and stripping therefore
 * happen in one operation, before React paints the authenticated application.
 */
export interface SignUpGrant {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface SignUpCallbackError {
  code: string
  description: string
}

export interface SignUpReadResult {
  grant: SignUpGrant | null
  error: SignUpCallbackError | null
}

export function readSignUpFromHash(
  loc: Pick<Location, 'hash' | 'pathname' | 'search'> | undefined =
    typeof window !== 'undefined' ? window.location : undefined,
  hist: Pick<History, 'replaceState'> | undefined =
    typeof window !== 'undefined' ? window.history : undefined,
): SignUpReadResult {
  const empty: SignUpReadResult = { grant: null, error: null }
  if (!loc?.hash || loc.hash.length < 2) return empty

  const params = new URLSearchParams(loc.hash.slice(1))
  // Recovery and federated callbacks have different ceremonies. Claiming one
  // here would silently turn this opt-in signup path into a generic auth back
  // door, so this parser owns exactly `type=signup` and nothing else.
  if (params.get('type') !== 'signup') return empty

  try {
    hist?.replaceState?.(null, '', `${loc.pathname}${loc.search}`)
  } catch {
    /* A non-browser host may not provide history; nothing is persisted here. */
  }

  const errorCode = params.get('error_code') ?? params.get('error')
  if (errorCode) {
    return {
      grant: null,
      error: {
        code: errorCode,
        description: params.get('error_description')?.replace(/\+/g, ' ')
          ?? 'That confirmation link is no longer valid.',
      },
    }
  }

  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')
  const expiresIn = Number(params.get('expires_in'))
  if (!accessToken || !refreshToken || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    return {
      grant: null,
      error: { code: 'invalid_signup_grant', description: 'That confirmation link is not complete. Request a new one.' },
    }
  }

  return { grant: { accessToken, refreshToken, expiresIn }, error: null }
}
